import { useCallback } from "react";

import { useQuery } from "@tanstack/react-query";
import { Group, MeasureData, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";
import { useDealActions } from "store/actions-context";
import {
  useDealDefaultCurrencySelect,
  useDealIdSelector,
  useMeasureParametersSelector
} from "store/selectors/deal-selectors/deal-selectors";
import { FeeType } from "store/types";

export type EnhancedMeasureData = MeasureData & { fee_type?: FeeType };

export const useMeasureDrawer = (dealIds: number[]) => {
  const { getDealRoundsWithInvestorsAction } = useDealActions();
  const currency = useDealDefaultCurrencySelect() || "USD";
  const measureParameters = useMeasureParametersSelector();
  const dealId = useDealIdSelector();

  const getMeasuresOfFetchable = useCallback(
    (fetchableName: string) =>
      measureParameters
        .filter((param) => param.calc_fields.includes(fetchableName))
        .map((param) => {
          const calcFieldIndex = param.calc_fields.indexOf(fetchableName);
          return {
            id: param.id,
            name: param.name,
            roles: [param.roles[calcFieldIndex]]
          };
        }),
    [measureParameters]
  );

  const commitmentMeasure = measureParameters.find((param) => param.prime_role === "commitment");

  const { data: rounds, isLoading: roundsLoading } = useQuery({
    queryKey: ["rounds", ...(dealIds || [])],
    queryFn: async () => {
      if (!dealIds?.length) return {};
      const dealRounds = await Promise.all(
        dealIds.map(async (dealId) => {
          return getDealRoundsWithInvestorsAction({ dealId, with_parent_round: true });
        })
      );

      return dealRounds.reduce<Record<number, MeasureDataRound[]>>((acc, rounds, index) => {
        acc[dealIds[index]] = rounds.map((round) => ({
          id: round.round_id,
          name: round.round_name,
          investors: round.investors,
          is_deleted: false
        }));
        return acc;
      }, {});
    },
    enabled: !!dealIds
  });

  const addMeasureToForm = useCallback(
    (round: MeasureDataRound, measureData: EnhancedMeasureData[], groups: Group[]) => {
      let groupId = groups.length ? Math.max(...groups.map((g) => g.id)) + 1 : 1;
      let keyId = measureData.length ? Math.max(...measureData.map((m) => m.key)) + 1 : 1;

      return (params: {
        id?: number;
        value: number;
        date: string;
        subDealId: number;
        measureId: number;
        customPaymentType?:
          | "principal_measure_data_id"
          | "interest_measure_data_id"
          | "pik_interest_measure_data_id"
          | "vat_measure_data_id"
          | "fee_measure_data_id";
        feeType?: FeeType;
      }) => {
        const { id, value, date, subDealId, measureId, customPaymentType, feeType } = params;
        if (round.investors.length) {
          round.investors.forEach((investor) => {
            measureData.push({
              investor_group: investor.investor_group || null,
              id: id ?? undefined,
              key: keyId++,
              group_id: groupId,
              value: (value / round.investors.length).toString(),
              currency: currency,
              date: date,
              description: null,
              investor_id: investor.id,
              investor_name: investor.name,
              round_id: round.id,
              accounting_date: null,
              enable_split: false,
              effective_range_from: null,
              effective_range_to: null,
              custom_payment_type: customPaymentType,
              files: null,
              ...(feeType && { fee_type: feeType })
            });
          });
        } else {
          measureData.push({
            investor_group: null,
            id: id ?? undefined,
            key: keyId++,
            group_id: groupId,
            value: value.toString(),
            currency: currency,
            date: date,
            description: null,
            investor_id: null,
            investor_name: null,
            round_id: round.id,
            accounting_date: null,
            enable_split: false,
            effective_range_from: null,
            effective_range_to: null,
            custom_payment_type: customPaymentType,
            files: null,
            ...(feeType && { fee_type: feeType })
          });
        }

        groups.push({
          id: groupId,
          deal_id: dealId || subDealId,
          sub_deal_id: dealId && dealId !== subDealId ? subDealId : null,
          measure_id: measureId,
          total_amount: value,
          original_measure_id: measureId,
          delete_measure_data_ids: [],
          original_group_measure_data_ids: measureData.filter((item) => item.id != null).map((item) => item.id!),
          files: null,
          descriptions: null
        });
        groupId++;
      };
    },
    [currency, dealId]
  );

  return {
    rounds,
    roundsLoading,
    addMeasureToForm,
    getMeasuresOfFetchable,
    commitmentMeasure
  };
};
